"use client";

import Image from "next/image";

import { ProductTag } from "@/components/common/tag";
import UpVoteButton from "@/components/common/upvote-button";
import { cn, formatImageUrl, getProductV2Path } from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import { TTag } from "@/types/tags";
import { ImageIcon, MapPinIcon, MessageSquare } from "lucide-react";
import { industriesServedData } from "@/modules/product-submission-v2/steps/product-information/components/industries-serve-data";
import { useQuery } from "@tanstack/react-query";
import { votesApi } from "@/frontend/service/votes/productv2-vote";
import { PRODUCT_VOTES_API } from "@/lib/constants";
import ProductListingLoadingState from "@/modules/home/components/product-listing/loading-state";
import { ModalType } from "@/types/modal-type";

export type TProductListingCardProps = {
	id: string;
	slug: string;
	logo?: string | null;
	name: string;
	tagline: string;
	description: string;
	tags?: TTag[];
	industriesServed?: string[];
	hqLocation?: string;
	rating?: string;
	reviewsCount?: number;
	commentsCount?: number;
	className?: string;
	isPremium?: boolean;
};

export const getProductVotesCount = async (productSlug: string) => {
	const res = await fetch(`${PRODUCT_VOTES_API}/${productSlug}`, {
		credentials: "include",
		next: { revalidate: 3 },
	});
	if (!res.ok) {
		console.error("Vote fetch failed:", await res.text());
		return { votesCount: 0, isUpvotedByUser: false, usersWhoVoted: [] };
	}
	const { data } = await res.json();
	return data;
};

export const showProductDetailsModal = (
	productName: string,
	slug: string,
	openModal: (type: ModalType, payload: { productSlug: string }) => void
) => {
	const url = getProductV2Path(slug);
	window.rudderanalytics?.track("click", {
		target_url: url,
		link_text: productName,
		type: "popup",
	});

	history.pushState({}, "", url);
	openModal(ModalType.ProductDetails, { productSlug: slug });
};

export const ProductTagsSectionInCard = (
	props: Pick<TProductListingCardProps, "industriesServed" | "tags" | "commentsCount" | "isPremium">
) => {
	const industryIconMap = new Map(industriesServedData.map((item) => [item.title, item.iconPath]));
	return (
		<div className="flex w-full items-center justify-between gap-4">
			<div className="flex items-center gap-2 overflow-auto">
				{props.industriesServed && props.industriesServed?.length > 0
					? props.industriesServed.slice(0, 3).map((industryName, index) => {
							const iconPath = industryIconMap.get(industryName);
							return props.isPremium ? (
								<ProductTag
									key={index}
									label={industryName}
									icon={iconPath}
									type="industries"
									isPremium={true}
								/>
							) : (
								<ProductTag
									key={index}
									label={industryName}
									icon={iconPath}
									type="industries"
								/>
							);
					  })
					: null}
				{props.tags && props.tags?.length > 0
					? props.tags
							.slice(0, 3)
							.map((tag) =>
								props.isPremium ? (
									<ProductTag
										key={tag.id}
										label={tag.name}
										slug={tag.name}
										type="tags"
										isPremium={true}
									/>
								) : (
									<ProductTag
										key={tag.id}
										label={tag.name}
										slug={tag.name}
										type="tags"
									/>
								)
							)
					: null}
			</div>

			<div className="flex items-center justify-start gap-1 text-gray-400">
				<span>
					<MessageSquare className="h-4 w-4" />
				</span>
				<span className="text-sm">{props.commentsCount}</span>
			</div>
		</div>
	);
};

export const ProductListingCard = ({
	id,
	slug,
	logo,
	name,
	tagline,
	description,
	rating,
	reviewsCount,
	hqLocation,
	className,
	...props
}: TProductListingCardProps) => {
	const { openModal } = useModal();

	const formattedLogoUrl = formatImageUrl(logo);

	// To show special skin / background to premium products
	const premiumProducts: string[] = [
		"REdistribute",
		"Camphor Property Search",
		"Hello Data.ai",
		"efizbo",
		"Gena",
		"Ask The Agent",
		"AgentMatch.AI",
		"Home Data Solutions",
		"Deed",
	];
	const showPremiumProductBackground = (productName: string) => {
		return premiumProducts.includes(productName);
	};
	const productBackgroundClassName = showPremiumProductBackground(name)
		? "bg-[url('/images/premium-product-background-skin.png')] bg-cover bg-center bg-no-repeat"
		: "bg-white";

	const { data: votesData, isLoading: votesLoading } = useQuery({
		queryKey: ["votes", slug],
		queryFn: () => votesApi.getProductVotesCount(slug),
		staleTime: 5 * 60 * 1000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	if (votesLoading) return <ProductListingLoadingState />;

	return (
		<div
			className={cn(
				"space-y-5 rounded-xl border-b border-gray-200 bg-white px-2 py-6",
				"border-gray-200 md:border md:p-4 md:shadow-[0px_4px_14px_0px_rgba(0,_0,_0,_0.02)] lg:p-6",
				productBackgroundClassName,
				className
			)}
		>
			<div className="flex items-center justify-between gap-2">
				<div>
					<div className="flex items-center gap-3">
						<div>
							<div className="flex aspect-square h-20 w-20 items-center justify-center overflow-hidden rounded-lg border">
								{formattedLogoUrl ? (
									<Image
										src={formattedLogoUrl}
										alt={name}
										width={480}
										height={480}
										className="h-full w-full object-contain"
									/>
								) : (
									<ImageIcon className="h-5 w-5 text-gray-400" />
								)}
							</div>
						</div>

						<div className="text-left">
							<button
								className="mb-1 inline-block text-left font-bold leading-tight text-gray-900 md:text-xl md:leading-normal"
								onClick={() => showProductDetailsModal(name, slug, openModal)}
							>
								{name}
							</button>
							<p className="line-clamp-3 text-xs text-gray-600 md:text-sm">{tagline}</p>
							<div className="mt-1 hidden items-center gap-1 text-sm leading-4 text-gray-400 md:flex">
								<MapPinIcon className="h-3 w-3 text-blue-600" />
								{hqLocation}
							</div>
						</div>
					</div>

					<p className="hidden text-left text-gray-500">{description}</p>
				</div>

				<UpVoteButton
					id={id}
					name={name}
					slug={slug}
					upVotes={votesData?.data.score ?? 0}
					rating={rating || "0"}
					reviewsCount={reviewsCount || 0}
					isUpvoted={votesData?.data.isUpvotedByUser ?? false}
					keyToInvalidate={["votes", slug]}
				/>
			</div>
			<ProductTagsSectionInCard
				tags={props.tags}
				industriesServed={props.industriesServed}
				commentsCount={props.commentsCount}
			/>
		</div>
	);
};
